/* eslint-disable max-classes-per-file */
import { produce, setAutoFreeze } from 'immer';
import { parse, parseUrl } from 'query-string';

import AkFeatureGates from '@atlaskit/feature-gate-js-client';
import PlatformAnalyticsClient, { envType, tenantType, userType } from '@atlassiansox/analytics-web-client';
import OriginTracer from '@atlassiansox/origin-tracing';

import type { EnvironmentConfig } from '@adminhub/config';
import { getConfig, getIcServiceUrl, isIsolatedCloud, SERVICE_SUB_DOMAIN_URL } from '@adminhub/config';
import { withMockOverride } from '@adminhub/help';
import type { NavigateFunction } from 'react-router-dom';

import type {
  OperationalData,
  OperationalEvent,
  ScreenData,
  ScreenEvent,
  TrackData,
  TrackEvent,
  UFOOperationalEvent,
  UIData,
  UIEvent,
} from './new-analytics-types';
import { sanitizeUrl } from './sanitization';

function getCloudIdFromPath(pathname: string): string | undefined {
  return (pathname.match(/\/s\/(.*?)(\/|$)/i) || [])[1];
}

function getOrgIdFromPath(pathname: string): string | undefined {
  return (pathname.match(/\/o\/(.*?)(\/|$)/i) || [])[1];
}

const product = {
  siteAdmin: 'siteAdmin',
  adminHub: 'adminHub',
};

const GASV3_API_URL = '/gateway/api/gasv3/api/v1';

const environmentType: EnvironmentConfig = {
  local: envType.LOCAL,
  ddev: envType.DEV,
  'stg-east': envType.STAGING,
  'stg-fedm-east': envType.STAGING,
  'stg-ic': envType.STAGING,
  'prod-east': envType.PROD,
  'prod-west': envType.PROD,
  'prod-fedm-east': envType.PROD,
  'prod-ic': envType.PROD,
};

type Event = UIEvent | ScreenEvent | TrackEvent | OperationalEvent;

export interface AnalyticsClient {
  init(): void;
  initOriginTracingHandlers(): void;
  setOrgId(orgId: string): void;

  sendUIEvent(e: UIEvent, onEventSent?: any): void; // onEventSent is an exposed onEventSent from '@atlassiansox/analytics-web-client'. Will be called when Event is sent.
  sendScreenEvent(e: ScreenEvent, onEventSent?: any): void; // onEventSent is an exposed onEventSent from '@atlassiansox/analytics-web-client'. Will be called when Event is sent.
  sendTrackEvent(e: TrackEvent, onEventSent?: any): void; // onEventSent is an exposed onEventSent from '@atlassiansox/analytics-web-client'. Will be called when Event is sent.
  sendOperationalEvent(e: OperationalEvent, onEventSent?: any): void;

  getAnalyticsWebClient(): any;
}

class AnalyticsWebClient implements AnalyticsClient {
  private client: any;
  private originTracingAttributes: Record<string, string> = {};

  constructor(
    client?: any,
    private getPathname: () => string = () => window.location.pathname,
    navigate?: NavigateFunction,
  ) {
    this.client = client || AnalyticsWebClient.createClient(navigate);
  }

  public static createClient = (navigate?: NavigateFunction) =>
    new PlatformAnalyticsClient(
      {
        env: environmentType[getConfig().analyticsClientEnvironment],
        product: 'admin',
        subproduct: product.adminHub,
        version: '1.0.0',
        locale: 'en-US',
        ic: isIsolatedCloud() ? 'true' : undefined, // TODO: final value underdetermined, may be specific IC environmnet identifier
      },
      {
        // function to remove atlOrigin query parameter in the URL
        historyReplaceFn: (url: string) => {
          const { pathname, search, hash } = new URL(url);
          if (navigate) {
            navigate({ pathname, search, hash }, { replace: true });
          }
        },
        apiHostProtocol: typeof window === 'undefined' ? 'http' : window.location.protocol.slice(0, -1),
        disableCookiePersistence: true,
        disableMetadataCalls: isIsolatedCloud(), // This is turned off for now since it is not enabled in server-side for IC's (which will be fixed later)
        apiHost: isIsolatedCloud()
          ? `${getIcServiceUrl(SERVICE_SUB_DOMAIN_URL.ADMINHUB_FRONTEND, { excludeHttpProtocol: true })}${GASV3_API_URL}`
          : undefined,
      },
    );

  public init = () => {
    // eslint-disable-next-line  @typescript-eslint/no-floating-promises
    this.setAtlassianAccountId();
  };

  public static create(navigate?: NavigateFunction): AnalyticsWebClient {
    return new AnalyticsWebClient(undefined, undefined, navigate);
  }

  public initOriginTracingHandlers = () => {
    this.client.setOriginTracingHandlers({
      atlOrigin: (encodedOrigin: string) => {
        const origin = OriginTracer.fromEncoded(encodedOrigin);

        if (origin.isValid() && !origin.isMalformed() && origin.product) {
          this.originTracingAttributes = { originProduct: origin.product };
        }

        return {
          originTracingAttributes: origin.toAnalyticsAttributes(),
          ...(!origin.isMalformed() && { taskSessionId: origin.id }),
        };
      },
    });
  };

  public isUFOEvent = (event: OperationalEvent | UFOOperationalEvent): event is UFOOperationalEvent =>
    event.tags?.includes('observability') || false;

  public getAnalyticsWebClient = () => this.client;

  public sendUIEvent = (e: UIEvent, onEventSent?: any) => {
    this.sendEvent(e, this.client.sendUIEvent.bind(this.client), onEventSent);
  };

  public sendScreenEvent = (e: ScreenEvent, onEventSent?: any) => {
    this.sendEvent(e, this.client.sendScreenEvent.bind(this.client), onEventSent);
  };

  public sendTrackEvent = (e: TrackEvent, onEventSent?: any) => {
    this.sendEvent(e, this.client.sendTrackEvent.bind(this.client), onEventSent);
  };

  public sendOperationalEvent = (e: OperationalEvent | UFOOperationalEvent, onEventSent?: any) => {
    if (this.isUFOEvent(e)) {
      this.sendUFOEvent(e, this.client.sendOperationalEvent.bind(this.client), onEventSent);
    } else {
      this.sendEvent(e, this.client.sendOperationalEvent.bind(this.client), onEventSent);
    }
  };

  public setOrgId = (orgId: string) => {
    this.client.setOrgInfo(orgId);
  };

  public get referrerAnalytics() {
    if (document.referrer !== '') {
      const { url, query } = parseUrl(document.referrer);

      const referrerParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].reduce((params, key) => {
        if (query[key]) {
          params[key] = query[key];
        }

        return params;
      }, {});

      const referrerUrl = sanitizeUrl(url);

      return {
        referrerParams,
        referrerUrl,
      };
    }

    const { srcType } = parse(window.location.search);

    if (srcType) {
      return {
        referrerParams: null,
        referrerUrl: sanitizeUrl(srcType as string),
      };
    }

    return {
      referrerParams: null,
      referrerUrl: null,
    };
  }

  private sendEvent(
    e: Event,
    send: (data: UIData | ScreenData | TrackData | OperationalData, onEventSent?: any) => void,
    onEventSent?: any,
  ) {
    const cloudIdFromPath = getCloudIdFromPath(this.getPathname());
    const orgIdFromPath = getOrgIdFromPath(this.getPathname());

    const hasCloudId = !!(e.cloudId || cloudIdFromPath);
    const hasOrgId = !!(e.orgId || orgIdFromPath);

    // Only clear tenant info if this event has no tenant context to set.
    // Clearing before setting causes unnecessary metadata refreshes because
    // the clear→set cycle triggers the MetadataClient's property change scheduler.
    if (!hasCloudId && !hasOrgId) {
      this.client.clearTenantInfo();
    }

    setAutoFreeze(false);
    const finalEventData = produce(e.data, (draft) => {
      draft.attributes = {
        ...draft.attributes,
        ...this.referrerAnalytics,
        ...this.originTracingAttributes,
        languagePreference: window.navigator.language,
        languageMultiplePreferences: window.navigator.languages,
        path: window.location.pathname,
      };

      return draft;
    });
    setAutoFreeze(true);

    if (hasCloudId) {
      this.client.setTenantInfo(tenantType.CLOUD_ID, e.cloudId || cloudIdFromPath);
    }
    if (hasOrgId) {
      this.client.setOrgInfo(e.orgId || orgIdFromPath);
      this.client.setTenantInfo(tenantType.ORG_ID, e.orgId || orgIdFromPath);
    }

    if (process.env.NODE_ENV === 'test') {
      // do not send analytics while running unit tests
      return;
    }

    send(finalEventData, onEventSent);
  }

  private sendUFOEvent(e: UFOOperationalEvent, send: (data: OperationalData, onEventSent?: any) => void, onEventSent?: any) {
    const cloudIdFromPath = getCloudIdFromPath(this.getPathname());
    const orgIdFromPath = getOrgIdFromPath(this.getPathname());

    // Only clear tenant info if this event has no tenant context to set.
    // Clearing before setting causes unnecessary metadata refreshes because
    // the clear→set cycle triggers the MetadataClient's property change scheduler.
    if (!cloudIdFromPath && !orgIdFromPath) {
      this.client.clearTenantInfo();
    }

    const finalEventData = {
      action: e.action,
      actionSubject: e.actionSubject,
      source: e.source,
      tags: e.tags,
      attributes: {
        ...e.attributes,
        ...this.referrerAnalytics,
        ...this.originTracingAttributes,
        languagePreference: window.navigator.language,
        languageMultiplePreferences: window.navigator.languages,
        path: window.location.pathname,
      },
    };

    if (cloudIdFromPath) {
      this.client.setTenantInfo(tenantType.CLOUD_ID, cloudIdFromPath);
    }
    if (orgIdFromPath) {
      this.client.setOrgInfo(orgIdFromPath);
      this.client.setTenantInfo(tenantType.ORG_ID, orgIdFromPath);
    }

    if (process.env.NODE_ENV === 'test') {
      // do not send analytics while running unit tests
      return;
    }

    send(finalEventData, onEventSent);
  }

  private async setAtlassianAccountId() {
    try {
      const currentUserResponse = await fetch(`${getConfig().apiUrl}/me`, {
        method: 'GET',
      });

      if (!currentUserResponse.ok) {
        return;
      }

      const currentUser = await currentUserResponse.json();

      this.client.setUserInfo(userType.ATLASSIAN_ACCOUNT, currentUser.account_id);
    } catch (_) {
      // noop because we dont want to double-up on reporting errors
      // for the getCurrentUser call failing as we are already reporting those elsewhere
    }
  }
}

class AnalyticsEventWrapper extends AnalyticsWebClient {
  private analyticsWebClient: AnalyticsWebClient;
  private eventHandlers: { name: string; handler(...args: any[]): any }[] | undefined;

  constructor(client: any, getPathname: () => string = () => window.location.pathname, navigate?: NavigateFunction) {
    const sharedClient = client || AnalyticsEventWrapper.createClient(navigate);
    super(sharedClient, getPathname, navigate);
    this.analyticsWebClient = new AnalyticsWebClient(sharedClient, getPathname, navigate);
  }

  public static override create(navigate?: NavigateFunction): AnalyticsEventWrapper {
    return new AnalyticsEventWrapper(undefined, undefined, navigate);
  }

  public override init = (eventHandlers?: { name: string; handler(...args: any[]): any }[]) => {
    this.analyticsWebClient.init();
    if (eventHandlers) this.eventHandlers = eventHandlers;
  };

  public static override createClient = (navigate?: NavigateFunction) => AnalyticsWebClient.createClient(navigate);

  public override initOriginTracingHandlers = () => {
    this.analyticsWebClient.initOriginTracingHandlers();
  };

  public override getAnalyticsWebClient = () => this.analyticsWebClient.getAnalyticsWebClient();

  public override sendUIEvent = (e: UIEvent, onEventSent?: any) => {
    this.analyticsWebClient.sendUIEvent(e, onEventSent);

    if (!AkFeatureGates.checkGate('add-surveys-to-analytic-events')) return;

    this.eventHandlers?.forEach((eventHandler) => {
      if (eventHandler.name === 'createSurvey') {
        const surveyEventId = this.generateSurveyEventId(e.data.action, e.data.actionSubject, e.data.source, undefined);
        eventHandler.handler(surveyEventId, e);
      } else {
        eventHandler.handler();
      }
    });
  };

  public override sendTrackEvent = (e: TrackEvent, onEventSent?: any) => {
    this.analyticsWebClient.sendTrackEvent(e, onEventSent);

    if (!AkFeatureGates.checkGate('add-surveys-to-analytic-events')) return;

    this.eventHandlers?.forEach((eventHandler) => {
      if (eventHandler.name === 'createSurvey') {
        const surveyEventId = this.generateSurveyEventId(e.data.action, e.data.actionSubject, e.data.source, undefined);
        eventHandler.handler(surveyEventId, e);
      } else {
        eventHandler.handler();
      }
    });
  };

  public override sendOperationalEvent = (e: OperationalEvent, onEventSent?: any) => {
    this.analyticsWebClient.sendOperationalEvent(e, onEventSent);

    if (!AkFeatureGates.checkGate('add-surveys-to-analytic-events')) return;

    this.eventHandlers?.forEach((eventHandler) => {
      if (eventHandler.name === 'createSurvey') {
        const surveyEventId = this.generateSurveyEventId(e.data.action, e.data.actionSubject, e.data.source, undefined);
        eventHandler.handler(surveyEventId, e);
      } else {
        eventHandler.handler();
      }
    });
  };

  public override sendScreenEvent = (e: ScreenEvent, onEventSent?: any) => {
    this.analyticsWebClient.sendScreenEvent(e, onEventSent);

    if (!AkFeatureGates.checkGate('add-surveys-to-analytic-events')) return;

    this.eventHandlers?.forEach((eventHandler) => {
      if (eventHandler.name === 'createSurvey') {
        const surveyEventId = this.generateSurveyEventId(undefined, undefined, undefined, e.data.name);
        eventHandler.handler(surveyEventId, e);
      } else {
        eventHandler.handler();
      }
    });
  };

  public override setOrgId = (orgId: string) => {
    this.analyticsWebClient.setOrgId(orgId);
  };

  private generateSurveyEventId = (
    eventAction: string | undefined,
    eventActionSubject: string | undefined,
    eventSource: string | undefined,
    eventName: string | undefined,
  ): string => {
    const stack: string[] = [];
    if (eventAction) stack.push(eventAction);
    if (eventActionSubject) stack.push(eventActionSubject);
    if (eventSource) stack.push(eventSource);
    if (eventName) stack.push(eventName);

    return stack.join('_');
  };
}

const PickedAnalyticsWebClient = withMockOverride(AnalyticsEventWrapper, 'AnalyticsWebClient');

export { PickedAnalyticsWebClient as AnalyticsWebClient };
